import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { apiGetJson } from "../../utils/apiClient";

type Row = {
    shopId: string; // shopUrl / shopId
    shopName?: string;
    companyName?: string;
    yearMonth: string;
    firstDownloadedAt: string; // ISO
};

function fmtYmdHm(v?: string) {
    if (!v) return "-";
    const d = dayjs(v);
    return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : "-";
}

export default function SuperRmsBillingPage() {
    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const PRICE_PER_MONTH = 5000;

    useEffect(() => {
        (async () => {
            setLoading(true);
            setError("");
            try {
                const data = await apiGetJson("/api/super/rms-billing");
                setRows(Array.isArray(data) ? (data as Row[]) : []);
            } catch (e: any) {
                setError(e?.message ?? "取得に失敗しました");
                setRows([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const totalMonths = rows.length;
    const totalAmount = useMemo(() => totalMonths * PRICE_PER_MONTH, [totalMonths]);

    // コンパクト共通
    const th = "border px-2 py-1 text-[11px] font-semibold bg-gray-100 whitespace-nowrap sticky top-0 z-10";
    const td = "border px-2 py-1 text-xs align-top";
    const mono = "font-mono";

    return (
        <div className="h-full flex flex-col gap-2 p-2 overflow-hidden">
            <div className="shrink-0">
                <h1 className="text-base font-semibold leading-tight">RMS取得 課金確認</h1>
                <div className="text-[11px] text-gray-500 leading-tight">
                    件数: {loading ? "..." : totalMonths} ／ 合計: ¥{totalAmount.toLocaleString()}（¥{PRICE_PER_MONTH.toLocaleString()}/月）
                </div>
            </div>

            {error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {error}
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-auto border rounded bg-white">
                <table className="min-w-[900px] w-full border-collapse">
                    <colgroup>
                        <col style={{ width: "26rem" }} />
                        <col style={{ width: "16rem" }} />
                        <col style={{ width: "16rem" }} />
                        <col style={{ width: "8rem" }} />
                        <col style={{ width: "12rem" }} />
                        <col style={{ width: "8rem" }} />
                    </colgroup>

                    <thead>
                        <tr>
                            <th className={th}>ショップID</th>
                            <th className={th}>ショップ名</th>
                            <th className={th}>会社名</th>
                            <th className={th}>対象月</th>
                            <th className={th}>初回取得日時</th>
                            <th className={`${th} text-right`}>金額</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading && (
                            <tr>
                                <td className={`${td} text-gray-500`} colSpan={6}>
                                    読み込み中...
                                </td>
                            </tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr>
                                <td className={`${td} text-gray-500`} colSpan={6}>
                                    データがありません
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            rows.map((r, i) => (
                                <tr key={`${r.shopId}__${r.yearMonth}__${i}`} className="hover:bg-gray-50">
                                    <td className={`${td} ${mono}`}>
                                        <div className="truncate" title={r.shopId}>
                                            {r.shopId}
                                        </div>
                                    </td>

                                    <td className={td}>
                                        <div className="truncate" title={r.shopName || ""}>
                                            {r.shopName || "-"}
                                        </div>
                                    </td>

                                    <td className={td}>
                                        <div className="truncate" title={r.companyName || ""}>
                                            {r.companyName || "-"}
                                        </div>
                                    </td>

                                    <td className={`${td} text-center ${mono}`}>{r.yearMonth}</td>

                                    <td className={`${td} ${mono}`}>{fmtYmdHm(r.firstDownloadedAt)}</td>

                                    <td className={`${td} text-right ${mono}`}>
                                        ¥{PRICE_PER_MONTH.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <div className="shrink-0 text-[11px] text-gray-600">
                合計課金月数：<span className="font-semibold">{totalMonths}</span> ／ 合計金額：
                <span className="font-semibold"> ¥{totalAmount.toLocaleString()}</span>
            </div>
        </div>
    );
}
